import PropTypes from 'prop-types';
import { m } from 'framer-motion';
import { useEffect, useState } from 'react';
// @mui
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
// routes
import { useRouter, usePathname } from 'src/routes/hook';
// hooks
import { usePermissions } from '../hooks';
// assets
import { ForbiddenIllustration } from 'src/assets/illustrations';
// components
import { MotionContainer, varBounce } from 'src/components/animate';

/**
 * PermissionGuard - Protects routes based on user permissions
 * Only applies to CRM users. Non-CRM users will bypass permission checks.
 *
 * @param {node} children - The component to render if user has permission
 * @param {string} permission - The permission name to check (e.g., 'manage_users')
 * @param {string} accessType - Type of access required: 'read', 'write', or 'both' (default: 'read')
 * @param {object} sx - Additional styles for the permission denied container
 */
export default function PermissionGuard({ children, permission, accessType = 'read', sx }) {
  const { hasPermission, canAccessPath, isCRMUser } = usePermissions();
  const pathname = usePathname();
  const router = useRouter();
  const [checked, setChecked] = useState(false);

  useEffect(() => {

    // If not a CRM user, allow access
    if (!isCRMUser) {
      setChecked(true);
      return;
    }

    // Check permission
    let hasAccess = false;

    if (permission) {
      // Check by permission name
      hasAccess = hasPermission(permission, accessType);
    } else {
      // Check by current path
      hasAccess = canAccessPath(pathname, accessType);
    }
    setChecked(true);

    // Optional: Redirect to 403 page or dashboard if no access
    // Uncomment if you want automatic redirect instead of showing the message inline
    // if (!hasAccess && isCRMUser) {
    //   router.replace('/403');
    // }
  }, [permission, accessType, pathname, hasPermission, canAccessPath, isCRMUser, router]);

  if (!checked) {
    return null;
  }

  // If not a CRM user, always allow
  if (!isCRMUser) {
    return <>{children}</>;
  }

  // Check permission
  const hasAccess = permission
    ? hasPermission(permission, accessType)
    : canAccessPath(pathname, accessType);

  if (!hasAccess) {
    return (
      <Container component={MotionContainer} sx={{ textAlign: 'center', ...sx }}>
        <m.div variants={varBounce().in}>
          <Typography variant="h3" paragraph>
            Permission Denied
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <Typography sx={{ color: 'text.secondary' }}>
            You do not have permission to access this page.
            <br />
            {permission && `Required permission: ${permission} (${accessType} access)`}
            <br />
            Please contact your system administrator.
          </Typography>
        </m.div>

        <m.div variants={varBounce().in}>
          <ForbiddenIllustration
            sx={{
              height: 260,
              my: { xs: 5, sm: 10 },
            }}
          />
        </m.div>
      </Container>
    );
  }

  return <>{children}</>;
}

PermissionGuard.propTypes = {
  children: PropTypes.node,
  permission: PropTypes.string,
  accessType: PropTypes.oneOf(['read', 'write', 'both']),
  sx: PropTypes.object,
};
